import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Globe } from "@phosphor-icons/react";
import { Button, EmptyState, ExpandedRow, Field, Input, PageHeader, Select, Table, TBody, TD, TH, THead, TR } from "@ghezaa/ui";
import type { Market } from "@/api/client";
import { useSetUpMarket, useUpdateMarket } from "@/api/mutations";
import { useAvailableMarkets } from "@/api/queries";
import { useMarket } from "@/lib/market";

export type MarketsSearch = { open?: string; new?: boolean };

export function marketsSearch(search: Record<string, unknown>): MarketsSearch {
  return {
    ...(typeof search.open === "string" && search.open !== "" ? { open: search.open } : {}),
    ...(search.new === true || search.new === "true" ? { new: true } : {}),
  };
}

/** Time zones the markets Ghezaa runs in use; any IANA zone the server knows is accepted. */
const zones = ["Africa/Cairo", "Asia/Riyadh", "Asia/Dubai"];

/**
 * The markets Sales serves: one of Core's markets, with the money and time zone its figures are counted in, linked to its
 * Business Central company. Setting one up needs Sales settings in every market.
 */
export function MarketsPage() {
  const { markets, answer } = useMarket();
  const search = useSearch({ strict: false }) as MarketsSearch;
  const navigate = useNavigate();
  const setSearch = (next: MarketsSearch) => void navigate({ to: ".", search: next as never, replace: true });
  const canSetUp = answer?.canSetUp ?? false;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Markets"
        description="Sales counts each market in its own money and time zone, from its own BC company."
        actions={
          canSetUp && !search.new && (
            <Button variant="primary" onClick={() => setSearch({ new: true })}>
              Set up a market
            </Button>
          )
        }
      />
      {search.new && canSetUp && <SetUpForm onDone={() => setSearch({})} />}
      {markets.length === 0 ? (
        !search.new && <EmptyState icon={Globe} title="No market set up yet" description="Set up the first market from Core's list, then link it to its Business Central company." />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Market</TH>
              <TH from="md">Money</TH>
              <TH from="md">Time zone</TH>
              <TH>Business Central</TH>
            </TR>
          </THead>
          <TBody>
            {markets.map((m) => {
              const selected = search.open === m.code;
              return [
                <TR key={m.code} data-state={selected ? "selected" : undefined}>
                  <TD className="w-full max-w-0">
                    {m.can.settings ? (
                      <button type="button" onClick={() => setSearch(selected ? {} : { open: m.code })} aria-expanded={selected} className="truncate text-start underline-offset-2 hover:underline">
                        {m.name}
                      </button>
                    ) : (
                      <span className="truncate">{m.name}</span>
                    )}
                    <span className="block text-xs text-ink-3">
                      <span className="font-mono">{m.code}</span>
                      <span className="md:hidden"> · {m.currency}</span>
                    </span>
                  </TD>
                  <TD from="md" className="whitespace-nowrap">{m.currency}</TD>
                  <TD from="md" className="whitespace-nowrap">{m.timeZone}</TD>
                  <TD className="whitespace-nowrap">
                    {m.can.erp ? (
                      <Link to="/$market/business-central" params={{ market: m.code }} className="underline-offset-2 hover:underline">
                        {m.company ?? "Link a company"}
                      </Link>
                    ) : (
                      (m.company ?? <span className="text-ink-3">Not linked</span>)
                    )}
                  </TD>
                </TR>,
                selected && (
                  <ExpandedRow key={`${m.code}-open`} colSpan={4}>
                    <EditForm market={m} onDone={() => setSearch({})} />
                  </ExpandedRow>
                ),
              ];
            })}
          </TBody>
        </Table>
      )}
    </div>
  );
}

function errorsOf(error: unknown): Record<string, string[]> {
  const errors = (error as { errors?: Record<string, string[]> } | null)?.errors;
  return errors ?? {};
}

function SetUpForm({ onDone }: { onDone: () => void }) {
  const available = useAvailableMarkets(true);
  const setUp = useSetUpMarket();
  const [code, setCode] = useState("");
  const [currency, setCurrency] = useState("");
  const [timeZone, setTimeZone] = useState(zones[0]);
  const errors = errorsOf(setUp.error);

  return (
    <form
      className="flex max-w-[480px] flex-col gap-4 border-y border-line-strong bg-canvas px-4 py-4"
      onSubmit={(e) => {
        e.preventDefault();
        setUp.mutate({ code, currency, timeZone }, { onSuccess: onDone });
      }}
    >
      <Field label="Market" htmlFor="new-market" error={available.isError ? available.error.message : errors.code?.[0]}>
        <Select id="new-market" value={code} onChange={(e) => setCode(e.target.value)} disabled={!available.data} autoFocus>
          <option value="">{available.isPending ? "Reading Core's markets…" : available.data?.length === 0 ? "Every market in Core is set up" : "Choose one of Core's markets"}</option>
          {available.data?.map((m) => (
            <option key={m.code} value={m.code}>
              {m.name} · {m.code}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Money" htmlFor="new-currency" error={errors.currency?.[0]}>
        <Input id="new-currency" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} placeholder="EGP" maxLength={3} />
      </Field>
      <Field label="Time zone" htmlFor="new-zone" error={errors.timeZone?.[0]}>
        <Select id="new-zone" value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
          {zones.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </Select>
      </Field>
      {setUp.isError && !Object.keys(errors).length && <p className="text-xs text-danger">{setUp.error.message}</p>}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={!code || setUp.isPending}>
          Set up
        </Button>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function EditForm({ market, onDone }: { market: Market; onDone: () => void }) {
  const update = useUpdateMarket(market.code);
  const [currency, setCurrency] = useState(market.currency);
  const [timeZone, setTimeZone] = useState(market.timeZone);
  const errors = errorsOf(update.error);
  const options = zones.includes(market.timeZone) ? zones : [market.timeZone, ...zones];

  return (
    <form
      className="flex max-w-[480px] flex-col gap-4 py-2"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({ currency, timeZone }, { onSuccess: onDone });
      }}
    >
      <Field label="Money" htmlFor={`currency-${market.code}`} error={errors.currency?.[0]}>
        <Input id={`currency-${market.code}`} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
      </Field>
      <Field label="Time zone" htmlFor={`zone-${market.code}`} error={errors.timeZone?.[0]}>
        <Select id={`zone-${market.code}`} value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
          {options.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </Select>
      </Field>
      {update.isError && !Object.keys(errors).length && <p className="text-xs text-danger">{update.error.message}</p>}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={update.isPending}>
          Save
        </Button>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
